import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { z } from "zod";

import { SilencedError } from "../../app/utilities/silenced-error";
import { useAccessControl } from "../../app/use-access-control";
import { processErrorsFromResponse } from "../../components/forms/form-helpers";
import type { QuickCreateInquirerFormValues } from "../../components/inquiry-form/quick-create-inquirer";
import type { QuickCreateStudentFormValues } from "../../components/inquiry-form/quick-create-student";
import { callLcosService, getHeaders, lcosEndpoint } from "../../data/lcosServices";
import { useAccessToken } from "../use-access-token";
import { useFormInput } from "../use-form-input";
import { inquiryQueryKeys } from "../use-inquiries/query-keys";
import { defaultFamilyContactDetail } from "./constants";
import { familyQueryKeys } from "./query-keys";
import { type FamilyDataConstants, getFamilyDataConstants } from "./use-family-contract-endpoints";
import { FAMILY_ROLE_INTERNAL_CODES, findFamilyRoleIdByInternalCode } from "./family-role-utils";
import {
  familyContactDetailResponsePayloadSchema,
  type FamilyContactDetail,
  type FamilyContactDetailResponsePayload,
  type FamilyContactDetailWithId
} from "./types";
import { familyMemberQueryKeys } from "./use-family-members";

const useOnContactCreationSuccess = () => {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries({ queryKey: familyQueryKeys.base });
    queryClient.invalidateQueries({ queryKey: inquiryQueryKeys.baseDashboard() });
  };
};

export const useCreateInquirer = () => {
  const { getToken } = useAccessToken();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      payload,
      centerId,
      familyId
    }: {
      payload: QuickCreateInquirerFormValues;
      centerId: string;
      familyId?: string;
    }) => {
      const accessToken = await getToken();
      const createdContact = await createContact(accessToken, { ...payload, contactType: "inquirer" }, { centerId });

      if (familyId?.trim()) {
        await assignCreatedContactRole({
          accessToken,
          queryClient,
          centerId,
          familyId,
          contactType: "inquirer",
          contactId: createdContact.id
        });
      }

      return createdContact;
    },
    onSuccess: useOnContactCreationSuccess()
  });
};

export const useCreateStudent = () => {
  const { getToken } = useAccessToken();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      payload,
      centerId,
      familyId
    }: {
      payload: QuickCreateStudentFormValues;
      centerId: string;
      familyId?: string;
    }) => {
      const accessToken = await getToken();
      const createdContact = await createContact(accessToken, { ...payload, contactType: "student" }, { centerId });

      if (familyId?.trim()) {
        await assignCreatedContactRole({
          accessToken,
          queryClient,
          centerId,
          familyId,
          contactType: "student",
          contactId: createdContact.id
        });
      }

      return createdContact;
    },
    onSuccess: useOnContactCreationSuccess()
  });
};

export const useFamilyContactDetail = (contactType: "inquirer" | "student", contactId?: number) => {
  const { getToken } = useAccessToken();
  const { activeCenterId } = useAccessControl();
  const queryClient = useQueryClient();

  const getContactDetailWrapper = async (id?: number) => {
    if (!(id && id > 0)) {
      return defaultFamilyContactDetail;
    }
    if (!activeCenterId?.trim()) {
      throw new Error("Missing center context: centerId");
    }
    return getContactDetail(await getToken(), activeCenterId, id);
  };

  const contactDetailQuery = useQuery({
    queryKey: familyQueryKeys.contactDetail(activeCenterId, contactId),
    queryFn: () => getContactDetailWrapper(contactId),
    select: data => ({ ...data, gradeId: data?.grade?.value })
  });

  const createOrUpdateContactMutator = useMutation({
    mutationFn: async ({
      id,
      payload: { age, ...payload },
      centerId,
      familyId,
      inquiryId
    }: {
      id?: number;
      payload: FamilyContactDetail;
      centerId?: string;
      familyId?: string;
      inquiryId?: string;
    }) => {
      const formattedPayload = {
        ...payload,
        ...(age ? { age: typeof age === "string" ? parseInt(age) : age } : {}),
        contactType,
        inquiryId
      };
      const token = await getToken();
      const resolvedCenterId = centerId ?? activeCenterId;

      if (id) {
        const missingFields: string[] = [];

        if (!resolvedCenterId?.trim()) {
          missingFields.push("centerId");
        }

        if (missingFields.length) {
          throw new Error(`Missing update contact context: ${missingFields.join(", ")}`);
        }

        return updateContact({
          accessToken: token,
          payload: formattedPayload,
          id,
          centerId: resolvedCenterId
        });
      }

      const missingFields: string[] = [];

      if (!resolvedCenterId?.trim()) {
        missingFields.push("centerId");
      }

      if (missingFields.length) {
        throw new Error(`Missing create contact context: ${missingFields.join(", ")}`);
      }

      const createdContact = await createContact(token, formattedPayload, {
        centerId: resolvedCenterId
      });

      if (familyId?.trim()) {
        await assignCreatedContactRole({
          accessToken: token,
          queryClient,
          centerId: resolvedCenterId,
          familyId,
          contactType,
          contactId: createdContact.id
        });
      }

      return createdContact;
    },
    onSuccess: (_data, payload) => {
      const resolvedCenterId = payload.centerId ?? activeCenterId;
      queryClient.invalidateQueries({ queryKey: familyQueryKeys.base });
      queryClient.invalidateQueries({ queryKey: inquiryQueryKeys.baseDashboard() });
      queryClient.invalidateQueries({ queryKey: familyMemberQueryKeys.detail(resolvedCenterId, payload.familyId) });
    }
  });

  const { buffer, onChangeValue, resetBuffer, setBuffer } = useFormInput(
    contactDetailQuery.data ?? defaultFamilyContactDetail
  );

  useEffect(() => {
    setBuffer(contactDetailQuery.data || defaultFamilyContactDetail);
  }, [contactDetailQuery.data]);

  return {
    buffer,
    onChangeValue,
    resetBuffer,
    contactDetail: contactDetailQuery.data,
    createOrUpdateContactMutator,
    isLoading: contactDetailQuery.isLoading
  };
};

const getContactDetail = (accessToken: string, centerId: string, contactId: number) => {
  return callLcosService<FamilyContactDetailWithId>(
    accessToken,
    `/api/centers/${centerId}/families/contacts/${contactId}`,
    "GET"
  );
};

type CreateInquirerRequestPayload = QuickCreateInquirerFormValues & { contactType: "inquirer" };
type CreateStudentRequestPayload = QuickCreateStudentFormValues & { contactType: "student" };
type ContactCreatePayload = FamilyContactDetail | CreateInquirerRequestPayload | CreateStudentRequestPayload;

interface CenterFamilyContactRouteOptions {
  centerId: string;
}

interface ContactRequiredPayload {
  firstName: string;
  lastName: string;
  email: string;
  age?: number;
  grade_id?: string;
}

interface ContactOptionalPayload {
  title?: string;
  suffix?: string;
  gender?: string;
  dob?: string;
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  state?: string;
  zipcode?: string;
  personalPhone?: string;
  homePhone?: string;
  businessPhone?: string;
  schoolName?: string;
  preferredContactMethod?: string;
}

type ContactPayload = ContactRequiredPayload & ContactOptionalPayload;
type ContactType = "inquirer" | "student";

interface FamilyRoleRequestPayload {
  contact_id: number;
  role_id: string;
}

const createContact = async (
  accessToken: string,
  payload: ContactCreatePayload,
  routeOptions: CenterFamilyContactRouteOptions
) => {
  const headers = getHeaders(accessToken);
  const payloadWithoutSchoolLookupUiFields = stripSchoolLookupUiFields(payload) as ContactCreatePayload;
  const { centerId } = routeOptions;
  const response = await fetch(lcosEndpoint + `/api/centers/${centerId}/families/contacts`, {
    method: "POST",
    headers,
    body: JSON.stringify(formatContactPayload(payloadWithoutSchoolLookupUiFields))
  });

  if (response.ok) {
    return (await response.json()) as FamilyContactDetailWithId;
  }

  return throwPossibleDuplicateError(response);
};

const addFamilyRoleForContact = async (
  accessToken: string,
  centerId: string,
  familyId: string,
  payload: FamilyRoleRequestPayload
) => {
  return callLcosService(accessToken, `/api/centers/${centerId}/families/${familyId}/roles`, "POST", payload);
};

const updateContact = async ({
  accessToken,
  id,
  centerId,
  payload
}: {
  accessToken: string;
  id: number;
  centerId: string;
  payload: FamilyContactDetail;
}) => {
  const headers = getHeaders(accessToken);
  const payloadWithoutSchoolLookupUiFields = stripSchoolLookupUiFields(payload) as ContactCreatePayload;
  const response = await fetch(lcosEndpoint + `/api/centers/${centerId}/families/contacts/${id}`, {
    method: "PUT",
    headers,
    body: JSON.stringify(formatContactPayload(payloadWithoutSchoolLookupUiFields))
  });
  if (!response.ok) {
    return throwPossibleDuplicateError(response);
  }

  return (await response.json()) as FamilyContactDetailWithId;
};

const formatFamilyContactDetailWithId = ({
  age,
  address_line1: addressLine1,
  address_line2: addressLine2,
  business_phone: businessPhone,
  display_name: displayName,
  first_name: firstName,
  home_phone: homePhone,
  last_name: lastName,
  personal_phone: personalPhone,
  preferred_contact_method: preferredContactMethod,
  grade,
  ...rest
}: FamilyContactDetailResponsePayload): FamilyContactDetailWithId => ({
  age: age ?? undefined,
  addressLine1,
  addressLine2,
  businessPhone,
  displayName,
  firstName,
  homePhone,
  lastName,
  personalPhone,
  preferredContactMethod,
  gradeId: grade?.value,
  ...rest
});

export class DuplicateContactError extends SilencedError {
  public readonly duplicateContacts: FamilyContactDetailWithId[];

  constructor(message: string, duplicateContacts: FamilyContactDetailResponsePayload[]) {
    super(message);
    this.name = "DuplicateContactError";
    this.duplicateContacts = duplicateContacts.map(formatFamilyContactDetailWithId);
  }
}

const stripSchoolLookupUiFields = <T extends object>(payload: T) => {
  const {
    schoolCode: _schoolCode,
    initialSchoolName: _initialSchoolName,
    ...rest
  } = payload as T & {
    schoolCode?: string;
    initialSchoolName?: string;
  };

  return rest;
};

const toNullablePositiveInteger = (value: unknown): number | null => {
  if (typeof value === "number" && Number.isFinite(value) && value > 0) {
    return Math.trunc(value);
  }
  if (typeof value === "string" && value.trim().length > 0) {
    const parsed = Number.parseInt(value, 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }
  return null;
};

const formatContactPayload = (payload: ContactCreatePayload): ContactPayload => {
  const normalizedPayload = payload as Partial<{
    age: number | string;
    gradeId: string;
    gender: string;
    dob: string;
    businessPhone: string;
    schoolName: string;
    preferredContactMethod: string;
  }> &
    ContactCreatePayload;

  const requiredPayload: ContactRequiredPayload = {
    firstName: normalizedPayload.firstName,
    lastName: normalizedPayload.lastName,
    email: normalizedPayload.email ?? ""
  };

  const normalizedAge = toNullablePositiveInteger(normalizedPayload.age);
  if (normalizedAge !== null) {
    requiredPayload.age = normalizedAge;
  }

  if (typeof normalizedPayload.gradeId === "string" && normalizedPayload.gradeId.trim().length > 0) {
    requiredPayload.grade_id = normalizedPayload.gradeId;
  }

  const optionalPayload: ContactOptionalPayload = {};

  const optionalStringFields: Array<keyof ContactOptionalPayload> = [
    "title",
    "suffix",
    "gender",
    "dob",
    "addressLine1",
    "addressLine2",
    "city",
    "state",
    "zipcode",
    "personalPhone",
    "homePhone",
    "businessPhone",
    "schoolName",
    "preferredContactMethod"
  ];

  optionalStringFields.forEach(field => {
    const value = normalizedPayload[field];
    if (typeof value === "string" && value.trim().length > 0) {
      optionalPayload[field] = value;
    }
  });

  return {
    ...requiredPayload,
    ...optionalPayload
  };
};

const findRoleIdForContactType = (roles: FamilyDataConstants["roles"], contactType: ContactType) => {
  const internalCode =
    contactType === "student" ? FAMILY_ROLE_INTERNAL_CODES.STUDENT : FAMILY_ROLE_INTERNAL_CODES.INQUIRER;
  return (
    findFamilyRoleIdByInternalCode(roles, internalCode, { activeOnly: true }) ??
    findFamilyRoleIdByInternalCode(roles, internalCode)
  );
};

const assignCreatedContactRole = async ({
  accessToken,
  queryClient,
  centerId,
  familyId,
  contactType,
  contactId
}: {
  accessToken: string;
  queryClient: ReturnType<typeof useQueryClient>;
  centerId: string;
  familyId: string;
  contactType: ContactType;
  contactId: number;
}) => {
  const cachedConstants = queryClient.getQueryData<FamilyDataConstants>(familyQueryKeys.constants());
  const familyConstants = cachedConstants ?? (await getFamilyDataConstants(accessToken));

  if (!cachedConstants) {
    queryClient.setQueryData(familyQueryKeys.constants(), familyConstants);
  }

  const roleId = findRoleIdForContactType(familyConstants.roles, contactType);
  if (!roleId) {
    throw new Error(`Unable to resolve role_id for ${contactType} while linking contact to family`);
  }

  await addFamilyRoleForContact(accessToken, centerId, familyId, {
    contact_id: contactId,
    role_id: roleId
  });
};

const throwPossibleDuplicateError = async (response: Response) => {
  if (response.status === 500) {
    throw new Error("Sorry, an unknown error occurred. Please try again later.");
  }

  await processErrorsFromResponse(response);

  const errorResponse = await response.json();

  const errorResponsePayload = z.object({}).passthrough().safeParse(errorResponse);
  if (!errorResponsePayload.success) {
    throw new Error(`non-OK api response: ${response.status} - ${response.statusText}`);
  }

  const detailResponse = z.object({ detail: z.string() }).safeParse(errorResponse);
  if (!detailResponse.success) {
    throw new Error(JSON.stringify(errorResponsePayload.data));
  }

  const duplicateContactsResponse = z
    .object({ body: z.object({ contacts: z.array(familyContactDetailResponsePayloadSchema) }) })
    .safeParse(errorResponse);
  if (!(response.status === 409 && duplicateContactsResponse.success)) {
    throw new Error(detailResponse.data.detail);
  }

  throw new DuplicateContactError(detailResponse.data.detail, duplicateContactsResponse.data.body.contacts);
};
